import { useState, useEffect } from 'react';
import api from '../api';
import {
  Shield,
  UserPlus,
  RefreshCw,
  Search,
  ChevronDown,
  ShieldCheck,
  ShieldAlert,
  User,
  Mail,
  Calendar,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'admin', label: 'Admin', icon: ShieldCheck, color: 'amber', description: 'Full access to all features' },
  { value: 'staff', label: 'Staff', icon: ShieldAlert, color: 'blue', description: 'Limited admin access' },
  { value: 'user', label: 'User', icon: User, color: 'gray', description: 'Regular user' },
];

function RoleBadge({ role }) {
  const roleConfig = ROLES.find(r => r.value === role) || ROLES[2];
  const colors = {
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colors[roleConfig.color]}`}>
      <roleConfig.icon size={12} />
      {roleConfig.label}
    </span>
  );
}


export default function Admins() {
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/admins');
      setAdmins(response.data.admins || []);
    } catch (err) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const searchUsers = async (term) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearching(true);
      const response = await api.get('/admin/users', { params: { search: term, limit: 10 } });
      // Filter out existing admins/staff
      const adminIds = admins.map(a => a.id);
      setSearchResults((response.data.users || []).filter(u => !adminIds.includes(u.id)));
    } catch (err) {
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePromote = async (userId, newRole) => {
    try {
      await api.post(`/admin/admins/${userId}/promote`, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
      setShowPromoteModal(false);
      setSelectedUser(null);
      setSearchTerm('');
      setSearchResults([]);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update role');
    }
  };

  const handleDemote = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'staff' : 'user';
    if (!confirm(`Change this user's role to ${newRole}?`)) return;
    await handlePromote(userId, newRole);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Admin Management</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchAdmins} className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
            <RefreshCw size={18} />
          </button>
          <button
            onClick={() => setShowPromoteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 font-medium"
          >
            <UserPlus size={18} /> Add Admin/Staff
          </button>
        </div>
      </div>


      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Admins</p>
              <p className="text-2xl font-bold text-white mt-1">
                {admins.filter(a => a.role === 'admin').length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-amber-500/10">
              <ShieldCheck size={24} className="text-amber-500" />
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Staff</p>
              <p className="text-2xl font-bold text-white mt-1">
                {admins.filter(a => a.role === 'staff').length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/10">
              <ShieldAlert size={24} className="text-blue-500" />
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Privileged Users</p>
              <p className="text-2xl font-bold text-white mt-1">{admins.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-500/10">
              <Shield size={24} className="text-green-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Admins List */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Admins & Staff</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Last Login</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {admins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-700/30">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-black font-bold">
                        {(admin.firstName?.[0] || admin.email[0]).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {admin.firstName && admin.lastName
                            ? `${admin.firstName} ${admin.lastName}`
                            : admin.email.split('@')[0]}
                        </p>
                        <p className="text-sm text-gray-400">{admin.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <RoleBadge role={admin.role} />
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-400">
                    {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      {admin.role === 'staff' && (
                        <button
                          onClick={() => handlePromote(admin.id, 'admin')}
                          className="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-400 rounded hover:bg-amber-500/20"
                        >
                          Promote to Admin
                        </button>
                      )}
                      {admin.role !== 'user' && (
                        <button
                          onClick={() => handleDemote(admin.id, admin.role)}
                          className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"
                        >
                          {admin.role === 'admin' ? 'Demote to Staff' : 'Remove Access'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {admins.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No admins or staff found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Promote Modal */}
      {showPromoteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Add Admin or Staff</h2>
              <button
                onClick={() => {
                  setShowPromoteModal(false);
                  setSelectedUser(null);
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-4">
              {/* Search */}
              <div className="relative mb-4">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search users by email or name..."
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400"
                />
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && !selectedUser && (
                <div className="mb-4 max-h-48 overflow-y-auto border border-gray-700 rounded-lg">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-700 text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm">
                        {(user.firstName?.[0] || user.email[0]).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white text-sm">
                          {user.firstName ? `${user.firstName} ${user.lastName}` : user.email}
                        </p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searching && (
                <p className="text-gray-400 text-sm mb-4">Searching...</p>
              )}

              {/* Selected User */}
              {selectedUser && (
                <div className="border border-gray-700 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-black font-bold text-lg">
                        {(selectedUser.firstName?.[0] || selectedUser.email[0]).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {selectedUser.firstName ? `${selectedUser.firstName} ${selectedUser.lastName}` : selectedUser.email}
                        </p>
                        <p className="text-sm text-gray-400">{selectedUser.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-3">Select a role to assign:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handlePromote(selectedUser.id, 'staff')}
                      className="flex flex-col items-center gap-2 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors"
                    >
                      <ShieldAlert size={24} className="text-blue-400" />
                      <span className="text-white font-medium">Staff</span>
                      <span className="text-xs text-gray-400">Limited access</span>
                    </button>
                    <button
                      onClick={() => handlePromote(selectedUser.id, 'admin')}
                      className="flex flex-col items-center gap-2 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-colors"
                    >
                      <ShieldCheck size={24} className="text-amber-400" />
                      <span className="text-white font-medium">Admin</span>
                      <span className="text-xs text-gray-400">Full access</span>
                    </button>
                  </div>
                </div>
              )}

              {!selectedUser && searchTerm.length < 2 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  Start typing to search for users to promote
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
